import type { ApiTaskSSEUpdate } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { ConversationTask } from '@atlassian/conversation-assistant-store/controllers/chat/types';

/**
 * Minimal shape needed to resolve a task id from channel merge data.
 * @see mergeConversationWithNetworkPriority — populates `tasks[].taskId` from API.
 */
type ConversationWithOptionalTasks = {
	tasks?: ConversationTask[];
};

export type WithLiveTask<T extends ConversationWithOptionalTasks> = T & {
	/** Latest Tasks SSE payload for this conversation's primary task, when known. */
	currentTask: ApiTaskSSEUpdate | null;
};

/**
 * Joins a chat-store conversation with the tasks store SSE map (`updatesByTaskId`).
 * Uses `conversation.tasks[0].taskId` when the channel payload included tasks from the API.
 *
 * Keeps merge logic in one place for side nav, full-screen list, and any other consumer.
 */
export function attachLiveTaskUpdate<T extends ConversationWithOptionalTasks>(
	conversation: T,
	updatesByTaskId: Map<string, ApiTaskSSEUpdate>,
): WithLiveTask<T> {
	const taskId = conversation.tasks?.[0]?.taskId;
	const taskUpdate = taskId ? updatesByTaskId.get(taskId) : undefined;

	return {
		...conversation,
		currentTask: taskUpdate ?? null,
	};
}
